import type { InitiateBrowserPaymentResponse } from '@medipass/web-sdk';

import type { PayPalCallbacks, PayPalFlowOptions } from './types';

export const isInstrumentDeclined = (error: unknown): boolean => {
  const response = error as { status?: number; data?: { errors?: unknown } } | undefined;
  return (
    response?.status === 400 &&
    Array.isArray(response.data?.errors) &&
    response.data.errors.includes('INSTRUMENT_DECLINED')
  );
};

// One flow is shared by the PayPal and Pay Later buttons for the current invoice/amount.
export function createPayPalFlow(options: PayPalFlowOptions): PayPalCallbacks & { dispose: () => void } {
  let generation = 0;
  let active = false;
  let disposed = false;
  let completed = false;
  let capturing = false;
  let order: InitiateBrowserPaymentResponse | undefined;
  let initiating: Promise<string> | undefined;

  const reset = () => {
    generation++;
    active = false;
    order = undefined;
    initiating = undefined;
    options.onBusyChange(false);
  };
  const fail = (error: unknown) => {
    if (disposed || completed) return;
    reset();
    options.onError(error);
  };

  return {
    async onClick(_data, actions) {
      if (disposed || active || completed) return actions.reject();
      const validationGeneration = generation;
      active = true;
      options.onBusyChange(true);
      try {
        if (!(await options.validate()) || disposed || generation !== validationGeneration || !active) {
          if (!disposed && generation === validationGeneration) reset();
          return actions.reject();
        }
        actions.resolve();
      } catch (error) {
        if (generation === validationGeneration) fail(error);
        actions.reject();
      }
    },
    async createOrder() {
      if (disposed || !active) throw new Error('This PayPal checkout is no longer active.');
      // actions.restart() may invoke createOrder again. Reuse the original order.
      if (order) return order.interactionId;
      if (!initiating) {
        const requestGeneration = generation;
        initiating = Promise.resolve()
          .then(() => options.initiate())
          .then(result => {
            if (disposed || !active || generation !== requestGeneration)
              throw new Error('This PayPal checkout is no longer active.');
            if (!result.interactionId || !result.orderId || !result.transactionId) {
              throw new Error('Unable to start PayPal checkout. Please try again.');
            }
            order = result;
            return result.interactionId;
          })
          .catch(error => {
            if (generation === requestGeneration) fail(error);
            throw error;
          });
      }
      return initiating;
    },
    async onApprove(data, actions) {
      if (disposed || completed || capturing) return;
      if (!active || !order || data.orderID !== order.interactionId) {
        fail(new Error('Unable to match the PayPal approval. Please try again.'));
        return;
      }
      capturing = true;
      try {
        await options.approve({ nonce: order.orderId, transactionId: order.transactionId, methodType: 'paypal' });
        completed = true;
        capturing = false;
      } catch (error) {
        capturing = false;
        if (disposed) return;
        if (isInstrumentDeclined(error)) {
          // Let the next onApprove retry /pays with the same IDs, without overlapping captures.
          try {
            await actions.restart();
          } catch (restartError) {
            fail(restartError);
          }
        } else {
          fail(error);
        }
      }
    },
    onCancel() {
      if (disposed || completed || capturing) return;
      reset();
      options.onCancel();
    },
    onError(error) {
      if (!capturing) fail(error);
    },
    dispose() {
      disposed = true;
      options.onBusyChange(false);
    },
  };
}
